import {Color} from '@benchling/nucleus-ui';
import {Button, Checkbox, createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import {MenuWidth, OptionContentType} from 'ui-library/menus/MenuConstants';
import MenuPopover from 'ui-library/menus/MenuPopover';
import Option from 'ui-library/menus/Option';
import PlainSearchInput, {BorderRadius, ClearType} from 'ui-library/PlainSearchInput';

export interface AuditLogFilterOption {
  value: string;
  label: string;
}

export interface AuditLogMultiSelectFilterDropdownContentProps<T extends AuditLogFilterOption> {
  options: Array<T>;
  selectedValues: Array<string>;
  onChange: (selectedValues: Array<string>) => void;
  renderOptionContent?: (option: T) => JSX.Element;
}

function filterOptionsByQuery<T extends AuditLogFilterOption>(options: Array<T>, query: string): Array<T> {
  if (!query.trim()) {
    return options;
  }
  const sanitizedQuery = query.trim().toLowerCase();
  return options.filter((opt) => opt.label.toLowerCase().includes(sanitizedQuery));
}

export default function AuditLogMultiSelectFilterDropdownContent<T extends AuditLogFilterOption>(
  props: AuditLogMultiSelectFilterDropdownContentProps<T>
): JSX.Element {
  const {options, selectedValues, onChange, renderOptionContent} = props;
  const {classes: checkboxClasses} = useCheckboxStyles();

  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => filterOptionsByQuery(options, searchQuery), [options, searchQuery]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const hasSelection = selectedValues.length > 0;
  // Only show the clear button if there is a selection and no search query
  const showClear = hasSelection && !searchQuery.trim();

  const handleClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const handleToggleOption = useCallback(
    (value: string) => {
      if (selectedSet.has(value)) {
        onChange(selectedValues.filter((v) => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    },
    [selectedSet, selectedValues, onChange]
  );

  return (
    <MenuPopover width={MenuWidth.XLARGE}>
      <div className={css(styles.searchRow)}>
        <PlainSearchInput
          borderRadius={BorderRadius.LARGE}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value);
          }}
          onEscape={() => {
            setSearchQuery('');
          }}
          placeholder="Search"
          clearType={ClearType.X}
          shouldAutoSelect={true}
        />
      </div>
      <div className={css(styles.scrollableList)}>
        {filteredOptions.length === 0 && searchQuery.trim() ? (
          <div className={css(styles.emptyState)}>
            <div className={css(styles.emptyStateLine1)}>No matches for "{searchQuery.trim()}"</div>
            <div className={css(styles.emptyStateLine2)}>Try a different keyword or check your spelling.</div>
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = selectedSet.has(option.value);
            return (
              <Option
                key={option.value}
                onSelect={() => {
                  handleToggleOption(option.value);
                }}
                contentClassName={css(styles.noPadding)}
                childrenClassName={css(styles.noPadding)}
                contentType={OptionContentType.PLAIN}
              >
                <Checkbox
                  size="md"
                  checked={isSelected}
                  onChange={() => {
                    handleToggleOption(option.value);
                  }}
                  label={renderOptionContent ? renderOptionContent(option) : <span>{option.label}</span>}
                  classNames={checkboxClasses}
                />
              </Option>
            );
          })
        )}
      </div>
      {showClear && (
        <div className={css(styles.clearRow)}>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            Clear
          </Button>
        </div>
      )}
    </MenuPopover>
  );
}

const styles = StyleSheet.create({
  noPadding: {
    padding: 0,
  },
  searchRow: {
    padding: '12px',
    borderBottom: `1px solid ${Color.GRAY_200}`,
  },
  scrollableList: {
    overflowY: 'auto',
    maxHeight: '40vh',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px',
    textAlign: 'center',
  },
  emptyStateLine1: {
    color: Color.GRAY_900,
    fontSize: 12,
    fontWeight: 500,
  },
  emptyStateLine2: {
    color: Color.GRAY_500,
    fontSize: 10,
    marginTop: 4,
  },
  clearRow: {
    borderTop: `1px solid ${Color.GRAY_200}`,
    padding: '4px 0px',
  },
});

const useCheckboxStyles = createStyles(() => ({
  root: {
    width: '100%',
    cursor: 'pointer',
    pointerEvents: 'none',
    padding: '8px 12px',
  },
  body: {
    display: 'flex',
    alignItems: 'center',
  },
}));
